import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { format } from "date-fns";
import { RowSelectionState } from "@tanstack/react-table";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Button } from "@design-system/Button/Button";
import { ArrowCornerIcon } from "@icons";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { StepTrailBreadcrumbs } from "@components/breadcrumbs/StepTrailBreadcrumbs";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    ResourceType,
    isHistoryDetailsResponse,
    isSourceIdGroupedResponse,
} from "@common/types/auditLog";
import {
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import { View } from "@common/types/views/views";
import { pluralizeWithCount } from "@common/helpers/string";
import { getSourceTypeTemplate } from "@common/constants/history-source-types";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { getRevertedFilterParams } from "@hooks/view/useHistoryPageFiltersCache";
import { addTimeZoneOffset } from "@helpers/date";
import { getShowRevertedParam } from "../../views/historyViewConfig";
import { useTransactionSelectionActions } from "../transactions/useTransactionSelectionActions";
import { isAccrualApproveSourceType } from "../../utils/accrualApproveSourceTypes";
import { historyDrawerContext } from "../../historyDrawerContext";
import {
    RevertSourceTarget,
    useHistorySourceRevert,
} from "../../hooks/useHistorySourceRevert";
import { RevertPreviewQuestion } from "../../components/RevertPreviewQuestion";
import { getActivityDescription } from "../../utils/getActivityDescription";
import {
    filterDisplayableDiffs,
    getFieldLabel,
    HistoryTransactionDetailsRow,
} from "../transactions/views/historyTransactionDetailsViewConfig";
import { excludeCategoryDiffs } from "../utils/historyDetailsHelpers";
import { HistoryUpdatedTransactionDetails } from "../HistoryUpdatedTransactionDetails";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import styles from "../split/SplitHistoryDrawer.module.scss";
import {
    batchGroupsViewConfig,
    BatchGroupColumn,
    BatchGroupRow,
    BatchGroupTableMeta,
} from "./views/batchGroupsViewConfig";
import { defaultBatchGroupsView } from "./views/defaultBatchGroupsView";

const BREADCRUMB_STEPS = [
    { id: "groups" as const, label: "Sources" },
    { id: "details" as const, label: "Details" },
];

const DETAILS_PAGE_SIZE = 20;

export const BatchHistoryDrawer: React.FC = () => {
    const { open, historyRow, revertedFilter, closeDrawer } =
        useContext(historyDrawerContext);
    const { enabled: isGlFirst } = useIsGlFirst();
    const revertedFilterParams = useMemo(() => {
        const base = getRevertedFilterParams(revertedFilter);
        const forceShowReverted = getShowRevertedParam(historyRow ?? null);
        if (forceShowReverted && !base.revertedOnly) {
            return { ...base, showReverted: true };
        }
        return base;
    }, [revertedFilter, historyRow]);

    const [isOpen, setIsOpen] = useState(false);
    const [groupsSearch, setGroupsSearch] = useState("");
    const [detailsSearch, setDetailsSearch] = useState("");
    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
        null,
    );

    const {
        revertTarget,
        requestRevert,
        cancelRevert,
        confirmRevert,
        previewCount,
        previewFields,
        isPreviewLoading,
    } = useHistorySourceRevert<RevertSourceTarget>(historyRow);

    const [groupsView, setGroupsView] = useState<View>(defaultBatchGroupsView);
    const [detailsPage, setDetailsPage] = useState(1);
    const [transactionRowSelection, setTransactionRowSelection] =
        useState<RowSelectionState>({});

    const shouldShowContent = isOpen || open;
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const { data: groupsResponse, isLoading: isGroupsLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            resourceType: historyRow?.resourceType,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            entityIds: historyRow?.entityIds,
            changeTypes: historyRow?.changeTypes,
            fields: historyRow?.fields,
            groupBySourceId: true,
            ...revertedFilterParams,
            page: 1,
            limit: 250,
            options: {
                enabled:
                    shouldShowContent &&
                    !!historyRow &&
                    !!historyRow.sourceType,
            },
        });

    const groupRows = useMemo<BatchGroupRow[]>(() => {
        if (!groupsResponse || !isSourceIdGroupedResponse(groupsResponse)) {
            return [];
        }
        return groupsResponse.groups.map((g) => ({
            id: g.sourceId,
            sourceId: g.sourceId,
            count: g.count,
            date: new Date(g.latestDate),
            isReverted: g.isReverted,
            ruleConditions: g.ruleConditions,
        }));
    }, [groupsResponse]);

    const filteredGroupRows = useMemo(() => {
        if (!groupsSearch) {
            return groupRows;
        }
        const term = groupsSearch.toLowerCase();
        return groupRows.filter((row) => {
            const dateStr = format(row.date, fullDateFormatWithTime);
            return dateStr.toLowerCase().includes(term);
        });
    }, [groupRows, groupsSearch]);

    const shouldSkipGroups = useMemo(() => {
        if (isGroupsLoading) {
            return false;
        }
        return groupRows.length === 1;
    }, [isGroupsLoading, groupRows.length]);

    useEffect(() => {
        if (
            open &&
            shouldSkipGroups &&
            !selectedSourceId &&
            groupRows.length === 1
        ) {
            setSelectedSourceId(groupRows[0].sourceId);
        }
    }, [open, shouldSkipGroups, selectedSourceId, groupRows]);

    const { data: detailsResponse, isLoading: isDetailsLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            resourceType: historyRow?.resourceType,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            entityIds: historyRow?.entityIds,
            changeTypes: historyRow?.changeTypes,
            fields: historyRow?.fields,
            sourceIds: selectedSourceId ? [selectedSourceId] : undefined,
            search: detailsSearch || undefined,
            page: detailsPage,
            limit: DETAILS_PAGE_SIZE,
            ...revertedFilterParams,
            options: {
                enabled:
                    shouldShowContent &&
                    !!selectedSourceId &&
                    !!historyRow &&
                    !!historyRow.sourceType,
            },
        });

    const detailsData = useMemo(() => {
        if (!detailsResponse || !isHistoryDetailsResponse(detailsResponse)) {
            return { items: [], total: 0, totalPages: 1 };
        }
        return {
            items: detailsResponse.data,
            total: detailsResponse.total,
            totalPages: detailsResponse.totalPages,
        };
    }, [detailsResponse]);

    const selectedFields = useMemo(
        () => historyRow?.fields ?? [],
        [historyRow?.fields],
    );

    const detailTableData = useMemo<HistoryTransactionDetailsRow[]>(
        () =>
            detailsData.items
                .filter(
                    (log: HistoryDetailsItem) =>
                        filterDisplayableDiffs(log.diff).length > 0 ||
                        log.changeType !== AuditLogChangeType.UPDATE,
                )
                .flatMap((log: HistoryDetailsItem) => {
                    let displayableDiffs = filterDisplayableDiffs(log.diff);

                    if (selectedFields.length > 0) {
                        displayableDiffs = displayableDiffs.filter(
                            (diff: AuditLogDiff) =>
                                selectedFields.includes(diff.field),
                        );
                    }

                    if (isGlFirst) {
                        displayableDiffs =
                            excludeCategoryDiffs(displayableDiffs);
                    }

                    if (
                        displayableDiffs.length === 0 &&
                        selectedFields.length === 0
                    ) {
                        return [
                            {
                                id: log.id,
                                logDate: log.createdAt,
                                date: log.resource?.date
                                    ? addTimeZoneOffset(
                                          new Date(log.resource.date as string),
                                      )
                                    : log.createdAt,
                                changeType: log.changeType,
                                resourceType: log.resourceType,
                                resourceId: log.resourceId,
                                resource: log.resource,
                                field: "",
                                fieldLabel: "",
                                oldValue: {
                                    value: null,
                                    valueLabel: undefined,
                                },
                                newValue: {
                                    value: null,
                                    valueLabel: undefined,
                                },
                                financialAccountId:
                                    log.resource?.financialAccountId,
                                amount: log.resource?.amount,
                                entityId:
                                    log.resource?.entityId ?? log.entityId,
                            },
                        ];
                    }

                    return displayableDiffs.map((diff: AuditLogDiff) => ({
                        id: log.id,
                        logDate: log.createdAt,
                        date: log.resource?.date
                            ? addTimeZoneOffset(
                                  new Date(log.resource.date as string),
                              )
                            : log.createdAt,
                        changeType: log.changeType,
                        resourceType: log.resourceType,
                        resourceId: log.resourceId,
                        resource: log.resource,
                        field: diff.field,
                        fieldLabel: getFieldLabel(diff.field) ?? "",
                        oldValue: diff.before,
                        newValue: diff.after,
                        financialAccountId: log.resource?.financialAccountId,
                        amount: log.resource?.amount,
                        entityId: log.resource?.entityId ?? log.entityId,
                    }));
                }),
        [detailsData.items, selectedFields, isGlFirst],
    );

    useEffect(() => {
        setTransactionRowSelection({});
    }, [selectedSourceId]);

    const hasTransactionRows = useMemo(
        () =>
            detailTableData.some(
                (row) => row.resourceType === ResourceType.TRANSACTION,
            ),
        [detailTableData],
    );

    const { selectedTransactionIds, openTransactions: handleOpenTransactions } =
        useTransactionSelectionActions({
            mode: "diff",
            rows: detailTableData,
            rowSelection: transactionRowSelection,
            historyRow: selectedSourceId ? (historyRow ?? null) : null,
            sourceIds: selectedSourceId ? [selectedSourceId] : undefined,
        });

    const selectedGroup = useMemo(
        () =>
            selectedSourceId
                ? groupRows.find((row) => row.sourceId === selectedSourceId)
                : undefined,
        [groupRows, selectedSourceId],
    );

    const handleClose = useCallback(() => {
        setGroupsSearch("");
        setDetailsSearch("");
        setSelectedSourceId(null);
        setDetailsPage(1);
        closeDrawer();
    }, [closeDrawer]);

    const handleSelectGroup = useCallback((row?: BatchGroupRow) => {
        if (!row) {
            return;
        }
        setSelectedSourceId(row.sourceId);
        setDetailsSearch("");
        setDetailsPage(1);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedSourceId(null);
        setDetailsSearch("");
        setDetailsPage(1);
        if (shouldSkipGroups) {
            closeDrawer();
        }
    }, [shouldSkipGroups, closeDrawer]);

    const handleBreadcrumbStepChange = useCallback(
        (stepId: "groups" | "details") => {
            if (stepId === "groups") {
                handleCloseDetails();
            }
        },
        [handleCloseDetails],
    );

    const detailsBreadcrumbSteps = useMemo(() => {
        if (shouldSkipGroups) {
            return [BREADCRUMB_STEPS[1]];
        }
        return BREADCRUMB_STEPS;
    }, [shouldSkipGroups]);

    const isRuleSource =
        historyRow?.sourceType === SQSProcessName.TRANSACTION_RULE_APPLICATION;

    const isRevertableSourceType =
        isRuleSource ||
        historyRow?.sourceType === APIProcessName.TRANSACTION_BULK_UPDATE ||
        isAccrualApproveSourceType(historyRow?.sourceType);

    const canRevertDetails =
        isRevertableSourceType &&
        !historyRow?.revertedAt &&
        !!selectedSourceId &&
        !selectedGroup?.isReverted;

    const detailsRevertCount =
        selectedTransactionIds.length > 0
            ? selectedTransactionIds.length
            : (selectedGroup?.count ?? detailsData.total);

    const handleRequestDetailsRevert = useCallback(() => {
        if (!canRevertDetails || !selectedSourceId) {
            return;
        }
        requestRevert({
            sourceId: selectedSourceId,
            count: detailsRevertCount,
            resourceIds:
                selectedTransactionIds.length > 0
                    ? selectedTransactionIds.map(String)
                    : undefined,
        });
    }, [
        canRevertDetails,
        selectedSourceId,
        detailsRevertCount,
        selectedTransactionIds,
        requestRevert,
    ]);

    const effectiveGroupsView = useMemo<View>(() => {
        if (isRuleSource) {
            return groupsView;
        }
        return {
            ...groupsView,
            columns: groupsView.columns.filter(
                (column) => column.id !== BatchGroupColumn.CONDITIONS,
            ),
        };
    }, [groupsView, isRuleSource]);

    const groupNoun = (() => {
        if (
            historyRow?.sourceType ===
            SQSProcessName.TRANSACTION_RULE_APPLICATION
        ) {
            return "rule";
        }
        if (historyRow?.sourceType === APIProcessName.TRANSACTION_BULK_UPDATE) {
            return "bulk update";
        }
        if (isAccrualApproveSourceType(historyRow?.sourceType)) {
            return "approval";
        }
        return "source";
    })();

    const detailsResourceNoun =
        getSourceTypeTemplate(
            historyRow?.sourceType ?? "",
            historyRow?.resourceType,
            historyRow?.fields,
        )?.noun ?? "item";

    const groupsTableMeta: BatchGroupTableMeta = useMemo(
        () => ({
            callbacks: {
                onRevert:
                    isRevertableSourceType && !historyRow?.revertedAt
                        ? requestRevert
                        : undefined,
                onExpand: handleSelectGroup,
            },
        }),
        [
            isRevertableSourceType,
            historyRow?.revertedAt,
            requestRevert,
            handleSelectGroup,
        ],
    );

    const layer1HeaderAddonLeft = useMemo(() => {
        if (!historyRow) {
            return null;
        }

        const description = getActivityDescription(historyRow);

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={description}
            />
        );
    }, [historyRow]);

    const layer2HeaderAddonLeft = useMemo(() => {
        const breadcrumbs = (
            <StepTrailBreadcrumbs
                steps={detailsBreadcrumbSteps}
                currentStep="details"
                onStepChange={handleBreadcrumbStepChange}
            />
        );

        if (!historyRow || !selectedGroup) {
            return breadcrumbs;
        }

        const selectedGroupHistoryRow = {
            ...historyRow,
            count: selectedGroup.count,
            date: selectedGroup.date,
        };
        const description = getActivityDescription(selectedGroupHistoryRow);

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={description}
                date={selectedGroup.date}
                leadingContent={breadcrumbs}
            />
        );
    }, [
        historyRow,
        selectedGroup,
        detailsBreadcrumbSteps,
        handleBreadcrumbStepChange,
    ]);

    const groupsFooter = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <CommandIslandTotal>
                {pluralizeWithCount(groupNoun, filteredGroupRows.length)}
            </CommandIslandTotal>
            <div />
        </CommandIsland>
    );

    const renderGroupsContent = () => {
        if (isGroupsLoading || shouldSkipGroups) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredGroupRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            groupsSearch
                                ? "No matching sources found"
                                : "No sources found"
                        }
                    />
                </div>
            );
        }

        return (
            <ViewTable
                data={filteredGroupRows}
                view={effectiveGroupsView}
                config={batchGroupsViewConfig}
                onViewChange={setGroupsView}
                onRowClick={handleSelectGroup}
                tableMeta={groupsTableMeta}
                emptyView={() => <EmptyState header="No sources found" />}
                className={styles.historyDetailsView}
                footer={groupsFooter}
            />
        );
    };

    const showViewTransactionsCta = !!historyRow && hasTransactionRows;
    const showDetailsPagination = detailsData.totalPages > 1;
    const detailsFooter = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <div className={styles.footerLeft}>
                {showDetailsPagination && (
                    <Pagination
                        size="md"
                        currentPage={detailsPage}
                        pageCount={detailsData.totalPages}
                        onPageChange={setDetailsPage}
                    />
                )}
                <CommandIslandTotal>
                    {pluralizeWithCount(detailsResourceNoun, detailsData.total)}
                </CommandIslandTotal>
            </div>
            <div className={styles.footerActions}>
                {canRevertDetails && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleRequestDetailsRevert}
                    >
                        {selectedTransactionIds.length > 0
                            ? `Undo ${selectedTransactionIds.length} selected`
                            : `Undo all (${detailsRevertCount})`}
                    </Button>
                )}
                {showViewTransactionsCta && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleOpenTransactions}
                        leftIcon={<ArrowCornerIcon />}
                    >
                        {selectedTransactionIds.length > 0
                            ? `View ${selectedTransactionIds.length} selected`
                            : "View all transactions"}
                    </Button>
                )}
            </div>
        </CommandIsland>
    );

    const renderDetailsContent = () => {
        if (isDetailsLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (detailTableData.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            detailsSearch
                                ? "No matching changes found"
                                : "No changes found"
                        }
                    />
                </div>
            );
        }

        if (!historyRow) {
            return null;
        }

        return (
            <HistoryUpdatedTransactionDetails
                data={detailTableData}
                footer={detailsFooter}
                historyRow={historyRow}
                rowSelection={transactionRowSelection}
                onRowSelectionChange={setTransactionRowSelection}
            />
        );
    };

    return (
        <>
            {revertTarget && (
                <ConfirmationModal
                    title="Undo change?"
                    question={
                        <RevertPreviewQuestion
                            fallbackCount={revertTarget.count}
                            previewCount={previewCount}
                            previewFields={previewFields}
                            primaryFields={historyRow?.fields ?? []}
                            isPreviewLoading={isPreviewLoading}
                        />
                    }
                    show
                    onHide={cancelRevert}
                    onReject={cancelRevert}
                    onConfirm={
                        isPreviewLoading
                            ? () => Promise.resolve()
                            : async () => {
                                  const isPartial =
                                      (revertTarget?.resourceIds?.length ?? 0) >
                                      0;
                                  const ok = await confirmRevert();
                                  if (ok && !isPartial) {
                                      handleClose();
                                  }
                              }
                    }
                    yes="Undo"
                    yesBtnVariant="primary"
                    no="Cancel"
                    noBtnVariant="secondary"
                />
            )}
            <Drawer
                open={open}
                onClose={handleClose}
                direction="right"
                onAnimationEnd={setIsOpen}
                headerAddonLeft={layer1HeaderAddonLeft}
                size="half"
                noPadding
            >
                {shouldShowContent && historyRow && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={groupsSearch}
                                    onChange={(v) => setGroupsSearch(v ?? "")}
                                    placeholder="Search sources..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderGroupsContent()}
                        </div>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedSourceId}
                onClose={handleCloseDetails}
                direction="right"
                size="fullScreenWithNav"
                noPadding
                headerAddonLeft={layer2HeaderAddonLeft}
            >
                {selectedSourceId && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={detailsSearch}
                                    onChange={(v) => {
                                        setDetailsSearch(v ?? "");
                                        setDetailsPage(1);
                                    }}
                                    placeholder="Search changes..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderDetailsContent()}
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};
